import React from 'react';
import { Link } from 'react-router-dom';
import type { QuotationLineCatalogueOptions } from '../../lib/quotationApi';
import { describeLinePricing, lineOptionsErrorMessage } from './quotationLineOptions';

/**
 * The card over the Master Catalogue grid when it was opened from a quotation
 * line's catalogue link (BUSINESS_RULES#quotation-line-catalogue-options): which
 * line, what priced it and why, at what units, how many other rows there are,
 * and the way back.
 *
 * One labelled place per question, after the owner's review of the first version
 * (2026-09-14) — a bulleted list of sentences a reviewer had to read top to bottom
 * to find the one thing they came for. Only what needs checking is called out,
 * in amber. The "Priced from" block carries the same cyan rule as the highlighted
 * row, so the card and the grid read as one statement.
 *
 * Rendered from the SERVER's answer about the line, never from the link's own
 * query string — the link carries only an id and a line number, and a line
 * number can move — so what it says about the line is always the line as it
 * stands.
 */
export interface QuotationLineOptionsBannerProps {
  quotationId: string;
  lineNumber: string;
  options: QuotationLineCatalogueOptions | null;
  error: unknown;
  loading: boolean;
  /** The row ids the grid is actually showing. */
  shownRowIds: string[];
  onShowAllRows: () => void;
}

function Fact({
  label,
  accent = false,
  children,
}: {
  label: string;
  accent?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`min-w-0 ${accent ? 'border-l-4 border-cyan-600 pl-3' : ''}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-xs leading-[18px] text-slate-900">{children}</div>
    </div>
  );
}

export const QuotationLineOptionsBanner: React.FC<QuotationLineOptionsBannerProps> = ({
  quotationId,
  lineNumber,
  options,
  error,
  loading,
  shownRowIds,
  onShowAllRows,
}) => {
  const summary = options && !error ? describeLinePricing(options, shownRowIds) : null;
  const line = options && !error ? options.line : null;
  const quantity =
    line && line.quantity != null ? [String(line.quantity), line.uom].filter(Boolean).join(' ') : null;

  return (
    <div className="px-6 pt-3" data-testid="quotation-line-options-banner">
      <section
        aria-label="Catalogue options for a quotation line"
        className="rounded-lg border border-[#D7DBD9] bg-white shadow-sm"
      >
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-t-lg border-b border-[#E6E9E8] bg-[#F9FAF9] px-4 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <Link
              to={`/quotations/${encodeURIComponent(quotationId)}`}
              className="shrink-0 inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-cyan-700 hover:bg-slate-200 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 focus-visible:ring-offset-1"
              title="Back to the quotation"
              data-testid="quotation-line-options-quotation-link"
            >
              {options?.quotation.quotationNumber || 'Quotation'}
            </Link>
            <span className="shrink-0 text-xs text-slate-500">Line {line ? line.lineNumber : lineNumber}</span>
            {line && (
              <span
                className="min-w-0 truncate text-sm font-semibold text-slate-900"
                title={line.itemName || undefined}
              >
                {line.itemName || 'No item name'}
              </span>
            )}
            {quantity && <span className="shrink-0 text-xs text-slate-500">{quantity}</span>}
          </div>
          <button
            type="button"
            onClick={onShowAllRows}
            className="shrink-0 inline-flex h-8 items-center rounded-md border border-[#D7DBD9] bg-white px-3 text-xs font-semibold text-slate-900 transition-colors hover:bg-[#F3F5F4]"
          >
            Show all rows
          </button>
        </div>

        {loading && !options && !error && (
          <p className="px-4 py-3 text-xs text-slate-500">Loading this line’s catalogue options…</p>
        )}

        {error ? (
          <p className="px-4 py-3 text-xs font-semibold text-red-700" role="alert">
            {lineOptionsErrorMessage(error, lineNumber)}
          </p>
        ) : null}

        {summary && (
          <div className="space-y-3 px-4 py-3">
            <div className="grid gap-x-8 gap-y-3 sm:grid-cols-3">
              <Fact label="Priced from" accent={summary.highlighted}>
                <div className="font-semibold">{summary.pricedFrom.title}</div>
                {summary.pricedFrom.detail && <div className="text-slate-600">{summary.pricedFrom.detail}</div>}
                {summary.why && <div className="text-slate-600">{summary.why}</div>}
                {summary.reasoning && (
                  <div className="line-clamp-2 italic text-slate-500" title={summary.reasoning}>
                    “{summary.reasoning}”
                  </div>
                )}
              </Fact>
              <Fact label="Units">
                {summary.units.length > 0 ? (
                  <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2">
                    {summary.units.map((unit) => (
                      <React.Fragment key={unit.label}>
                        <dt className="text-slate-500">{unit.label}</dt>
                        <dd className="font-semibold">{unit.value}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                ) : (
                  <span className="text-slate-400">Not recorded</span>
                )}
              </Fact>
              <Fact label="Options">
                <div className="font-semibold">{summary.options.headline}</div>
                <div className="text-slate-600">{summary.options.detail}</div>
              </Fact>
            </div>

            {summary.supplierPrices && (
              <div data-testid="quotation-line-supplier-prices">
                <Fact label="Supplier prices">
                  <div className="text-slate-600">{summary.supplierPrices.rule}</div>
                  <ul className="mt-1 divide-y divide-[#E6E9E8] rounded-md border border-[#E6E9E8]">
                    {summary.supplierPrices.rows.map((row, index) => (
                      <li
                        key={`${row.supplier}-${index}`}
                        className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-4 px-2.5 py-1 sm:grid-cols-[minmax(0,1fr)_auto_auto]"
                      >
                        <span className="min-w-0 truncate font-semibold" title={row.supplier}>
                          {row.supplier}
                          {row.mark && (
                            <span className="ml-1.5 rounded bg-cyan-50 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-cyan-700">
                              {row.mark}
                            </span>
                          )}
                        </span>
                        <span className="text-right tabular-nums">{row.price}</span>
                        <span className="col-span-2 text-slate-500 sm:col-span-1 sm:text-right">{row.date}</span>
                      </li>
                    ))}
                  </ul>
                </Fact>
              </div>
            )}

            {summary.cautions.map((caution) => (
              <p
                key={caution}
                role="note"
                className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900"
              >
                <span aria-hidden="true">⚠</span>
                <span>{caution}</span>
              </p>
            ))}

            {summary.footnote && <p className="text-xs leading-4 text-slate-500">{summary.footnote}</p>}
          </div>
        )}
      </section>
    </div>
  );
};

export default QuotationLineOptionsBanner;
